import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type ToggleIconPos = 'left' | 'right';

export interface ToggleButtonChangeEvent {
    checked: boolean;
}

@Component({
    selector: 'app-toggle-button',
    templateUrl: './toggle-button.component.html',
    styleUrls: ['./toggle-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AppToggleButtonComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class AppToggleButtonComponent implements ControlValueAccessor {
    /** Prime-style API compatibility */
    @Input() onLabel = 'On';
    @Input() offLabel = 'Off';
    @Input() onIcon?: string;
    @Input() offIcon?: string;
    @Input() iconPos: ToggleIconPos = 'left';
    @Input() disabled = false;
    @Input() styleClass?: string;
    @Input() style?: Record<string, any>;

    @Output() onChange = new EventEmitter<ToggleButtonChangeEvent>();

    checked = false;

    private onCvaChange: (value: boolean) => void = () => {};
    private onCvaTouched: () => void = () => {};

    /** Template-safe touch handler */
    markTouched(): void {
        this.onCvaTouched();
    }

    writeValue(obj: any): void {
        this.checked = Boolean(obj);
    }

    registerOnChange(fn: any): void {
        this.onCvaChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onCvaTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    get computedButtonClass(): string {
        const cls: string[] = ['p-togglebutton', 'p-button', 'p-component'];
        if (this.checked) cls.push('p-highlight');
        if (this.disabled) cls.push('p-disabled');
        if (this.styleClass) cls.push(...this.styleClass.split(' ').filter(Boolean));
        return cls.join(' ');
    }

    get currentLabel(): string {
        return this.checked ? this.onLabel : this.offLabel;
    }

    get currentIcon(): string | null {
        const icon = this.checked ? this.onIcon : this.offIcon;
        return icon?.trim() ? icon.trim() : null;
    }

    get iconPositionClass(): string {
        return this.iconPos === 'right' ? 'p-button-icon-right' : 'p-button-icon-left';
    }

    toggle(): void {
        if (this.disabled) return;
        const next = !this.checked;
        this.checked = next;
        this.onCvaTouched();
        this.onCvaChange(next);
        this.onChange.emit({ checked: next });
    }
}

